import {useEffect, useRef, useState} from 'react';
import {GoogleMapsOverlay} from '@deck.gl/google-maps';
import {HeatmapLayer} from '@deck.gl/aggregation-layers';

// Gradiente original (80 cores) convertido para [r, g, b] do deck.gl
const colorRange = [
  [0, 255, 0],
  [0, 253, 40],
  [0, 250, 60],
  [0, 248, 77],
  [0, 245, 93],
  [0, 242, 107],
  [0, 239, 121],
  [0, 235, 135],
  [0, 232, 149],
  [0, 228, 163],
  [0, 225, 176],
  [0, 221, 190],
  [0, 217, 203],
  [0, 213, 216],
  [0, 209, 229],
  [0, 205, 242],
  [0, 201, 254],
  [0, 197, 255],
  [0, 193, 255],
  [0, 188, 255],
  [0, 184, 255],
  [0, 179, 255],
  [0, 175, 255],
  [0, 170, 255],
  [0, 165, 255],
  [0, 160, 255],
  [0, 155, 255],
  [0, 149, 255],
  [0, 143, 255],
  [0, 137, 255],
  [0, 130, 255],
  [0, 123, 255],
  [0, 115, 255],
  [0, 106, 255],
  [0, 97, 255],
  [0, 86, 255],
  [0, 74, 255],
  [0, 60, 255],
  [0, 41, 255],
  [0, 0, 255],
  [0, 0, 255],
  [68, 0, 250],
  [96, 0, 244],
  [116, 0, 238],
  [133, 0, 232],
  [147, 0, 226],
  [160, 0, 220],
  [171, 0, 213],
  [181, 0, 206],
  [191, 0, 200],
  [199, 0, 193],
  [207, 0, 186],
  [214, 0, 179],
  [220, 0, 172],
  [226, 0, 166],
  [232, 0, 159],
  [237, 0, 152],
  [241, 0, 145],
  [245, 0, 138],
  [249, 0, 132],
  [252, 0, 125],
  [255, 0, 119],
  [255, 0, 112],
  [255, 0, 106],
  [255, 0, 100],
  [255, 0, 94],
  [255, 0, 88],
  [255, 0, 82],
  [255, 0, 76],
  [255, 0, 70],
  [255, 0, 64],
  [255, 0, 59],
  [255, 0, 53],
  [255, 0, 47],
  [255, 0, 42],
  [255, 0, 35],
  [255, 0, 29],
  [255, 0, 21],
  [255, 0, 12],
  [255, 0, 0],
];

export default function Heatmap({
  maps,
  map,
  toggleRadius = false,
  toggleOpacity = false,
  toggleGradient = false,
  toggleHeatmap = false,
  points = [],
  withBounds = true,
}) {
  const overlayRef = useRef(null);

  const baseRadius = 35;
  const baseOpacity = 0.5;
  const zoomMultiplierBaseLine = 11;
  const radiusMultiplyRate = 0.3;
  const opacityMultiplyRate = 0.08;

  const [zoomRadiusMultiplier, setZoomRadiusMultiplier] = useState(1);
  const [zoomOpacityMultiplier, setZoomOpacityMultiplier] = useState(1);

  // Inicializa o overlay do deck.gl
  useEffect(() => {
    if (!map) return;

    const overlay = new GoogleMapsOverlay({interleaved: false});
    overlay.setMap(map);
    overlayRef.current = overlay;

    // Intervalo de zoom — idêntico ao original (1 segundo)
    const zoomInterval = setInterval(() => {
      if (!map) return;
      const zoom = map.getZoom();
      setZoomRadiusMultiplier(
        Math.max((zoom - zoomMultiplierBaseLine) * radiusMultiplyRate, 0) + 1,
      );
      setZoomOpacityMultiplier(
        Math.max((zoom - zoomMultiplierBaseLine) * opacityMultiplyRate, 0) + 1,
      );
    }, 1000);

    return () => {
      clearInterval(zoomInterval);
      overlay.setMap(null);
      overlay.finalize();
      overlayRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // fitBounds é feito pelo Map.jsx via prop locations — não duplicar aqui

  // Atualiza o layer quando dados ou props visuais mudam
  useEffect(() => {
    if (!overlayRef.current || !map) return;

    // Radius — lógica do original
    let radiusPixels;
    if (!toggleRadius) {
      radiusPixels = baseRadius * zoomRadiusMultiplier;
    } else {
      const zoom = map.getZoom();
      if (zoom < 16) radiusPixels = 5;
      if (zoom > 17) radiusPixels = 30;
    }
    if (!radiusPixels || radiusPixels === 0) radiusPixels = baseRadius;

    // Opacity — baseOpacity * zoomOpacityMultiplier (ou 0 se toggleOpacity)
    const opacity = toggleOpacity ? 0 : baseOpacity * zoomOpacityMultiplier;

    const layer = new HeatmapLayer({
      id: 'heatmap-layer',
      data: points.filter(p => p.latitude && p.longitude),
      getPosition: d => [d.longitude, d.latitude],
      getWeight: d => d.peso || 1,
      radiusPixels,
      intensity: toggleRadius ? 10 : 3,
      threshold: 0.005,
      colorRange: toggleGradient ? undefined : colorRange,
      opacity: Math.max(opacity, 0),
      visible: !toggleHeatmap,
    });

    overlayRef.current.setProps({layers: [layer]});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, toggleRadius, toggleOpacity, toggleGradient, toggleHeatmap, zoomRadiusMultiplier, zoomOpacityMultiplier]);

  return <></>;
}
